import { inRange } from "radashi";
import { useEffect, useRef } from "react";
import { byShortcut } from "../../../helpers/predicate";
import { useKeyboard } from "../../../hooks/useKeyboard";

type UseDialogHandlerArgs = {
  dismissible: boolean;
  onBackdropClick?: () => void;
  onCancel?: () => void;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  ref: React.RefObject<HTMLDialogElement | null>;
};

export function useDialogHandler({
  dismissible,
  onBackdropClick,
  onCancel,
  onOpenChange,
  open,
  ref,
}: UseDialogHandlerArgs) {
  const triggerRef = useRef<HTMLElement | null>(null);

  useKeyboard(byShortcut("Escape"), () => {
    if (dismissible && ref.current?.open) {
      ref.current.close();
    }
  });

  useEffect(() => {
    const dialogEl = ref.current;
    if (!dialogEl) return;

    if (open && !dialogEl.open) {
      const { activeElement } = document;
      triggerRef.current =
        activeElement instanceof HTMLElement ? activeElement : null;
      dialogEl.showModal();
    } else if (!open && dialogEl.open) {
      dialogEl.close();
    }

    function onDialogCancel(e: Event) {
      e.preventDefault();
      onCancel?.();
      onOpenChange?.(false);
    }

    function onDialogBackdropClick(event: MouseEvent) {
      if ((event.target as Element)?.closest('[data-slot="toast-region"]'))
        return;
      if (!dialogEl || !dismissible || !event.clientY || !event.clientX) return;
      const rect = dialogEl.getBoundingClientRect();
      const isInDialog =
        inRange(event.clientY, rect.top, rect.top + rect.height) &&
        inRange(event.clientX, rect.left, rect.left + rect.width);
      if (!isInDialog) {
        dialogEl.close();
        onBackdropClick?.();
      }
    }

    const controller = new AbortController();
    const signalOption = { signal: controller.signal };
    dialogEl.addEventListener("close", onDialogCancel, signalOption);
    dialogEl.addEventListener("click", onDialogBackdropClick, signalOption);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissible, open, ref]);

  // Unmounting an open dialog skips the native focus restoration of close()
  useEffect(
    () => () => {
      const trigger = triggerRef.current;
      const focusIsLost =
        !document.activeElement || document.activeElement === document.body;
      if (trigger?.isConnected && focusIsLost) trigger.focus();
    },
    [],
  );
}
